/**
 * Where To Buy V4 — 移动端交互控制器
 *
 * 设计目标（满足以下交互且无反跳 / 闪现 / 卡顿 / 露出后续内容）：
 *  1. 地图区域：单指滑动页面，双指操作地图（依赖 Google Maps `cooperative` 手势）。
 *  2. flow 阶段：dealerPanel 与地图一体，保留当前档位，随页面一起原生滚动。
 *  3. 页面上滑到本 section 钉顶后激活 sheet 阶段，滑盖支持 peek / half / full 三档独立拖拽。
 *  4. 在 peek 继续下拉，把手势交还原生滚动 → 平滑恢复上方内容到正常浏览。
 *  5. 全程面板使用同一套 translateY 几何（flow 与 sheet 的档位完全一致），不重排不切定位。
 *
 * 关键点：
 *  - section 在移动端始终 `position: sticky; top:0`，后置 spacer 只提供 sticky 范围；
 *    `--wtb-v4-vh` 初始化后锁定，只在宽度变化时重算，避免移动浏览器地址栏伸缩触发高度跳变；
 *    `--wtb-v4-fill-vh` 可在 sheet 固定后向下补满当前视口，但 sheet 顶部几何仍按锁定高度计算。
 *    sheet 激活后冻结 body 在触顶点（iOS WebKit 除外），避免后台 scrollY 继续累积或惯性越过本 section。
 *  - 钉顶（rect.top <= 0）瞬间进入 sheet 阶段；此时面板保留当前档位，无跳变。
 *  - sheet 阶段用 transform 驱动滑盖，触摸状态机分流：drag(拖盖) / list(列表滚动) / map(双指) / map-page(地图单指页面滚动) / release(下拉恢复)。
 *  - 恢复（release）先归位到触顶点，再用同一手势手动推进页面滚动，退出固定后面板回到 half 档。
 */
(function () {
  'use strict';

  var BP = 768;
  var PEEK_PX = 132; // peek 档露出高度（约一个搜索行）
  var TOP_GAP = 56; // full 档顶部为地图留出的高度
  var SNAP_MS = 650; // 吸附动画时长（需与 CSS 过渡一致）
  var RUNWAY_VH = 2.0; // sticky 范围（视口倍数），sheet 阶段不再让页面滚动消耗这段距离
  var DECIDE_PX = 4; // 判定手势方向所需的最小位移
  var MAP_PAGE_RELEASE_PX = 56; // 地图区域单指下拉恢复页面滚动前的缓冲距离
  var VEL = 0.35; // 快速轻扫速度阈值（px/ms）
  var REENGAGE_GAP = 8; // 恢复后离顶超过该距离即允许再次进入（避免边界处反复进入/限位假死）
  var FLOW_PRELOCK_MAX_PX = 72; // flow 阶段接近钉顶时提前接管，避免 fling 先绘制到后续内容
  var MAP_PADDING_LIFT_RATIO = 0.28; // 移动端额外底部安全区，让视觉中心上移避开 panel
  var MAP_PADDING_LIFT_MAX = 180;
  var MAP_CENTER_LIFT_RATIO = 0.42; // 重新居中时把目标点放到 panel 上方的视觉中心
  var MAP_CENTER_LIFT_MAX = 220;
  var RESIZE_WIDTH_EPS = 2; // 地址栏伸缩通常只改高度，同宽 resize 不重算 sheet 几何
  var RELEASE_REENGAGE_MS = 180; // 交还页面后若没有真实离顶，则自动恢复 sheet
  var KEYBOARD_VH_DELTA = 80; // iOS 键盘 / 地址栏视口变化阈值

  var nowMs = function () {
    return typeof performance !== 'undefined' && performance.now
      ? performance.now()
      : Date.now();
  };

  var clamp = function (v, a, b) {
    return Math.max(a, Math.min(b, v));
  };

  // iOS WebKit 在 body fixed 后不会稳定跟随地址栏/工具栏变化，仅用于跳过锁页。
  var IS_IOS_WEBKIT = (function () {
    var ua = navigator.userAgent || '';
    var platform = navigator.platform || '';
    return (
      /iP(hone|od|ad)/.test(platform) ||
      (/Mac/.test(platform) && navigator.maxTouchPoints > 1) ||
      /iPhone|iPad|iPod/.test(ua)
    );
  })();

  var mapBridge = { map: null, lastPaddingKey: '' };
  var controllers = [];
  var lastUserIntentAt = 0;

  function markUserIntent() {
    lastUserIntentAt = nowMs();
  }

  function hasRecentUserIntent() {
    return nowMs() - lastUserIntentAt < 1200;
  }

  ['pointerdown', 'touchstart', 'touchmove', 'mousedown', 'wheel', 'keydown'].forEach(function (eventName) {
    window.addEventListener(eventName, markUserIntent, { passive: true, capture: true });
  });

  // 对外 API（被 section liquid 中的 initMap 调用），保持兼容
  window.WhereToBuyV4 = {
    registerMap: function (map) {
      mapBridge.map = map;
      mapBridge.lastPaddingKey = '';
      controllers.forEach(function (c) {
        c.syncMapPadding();
      });
    },
    getGestureHandling: function () {
      // cooperative：单指交给页面/滑盖，双指操作地图
      return 'cooperative';
    },
    updateMapPadding: function () {
      controllers.forEach(function (c) {
        c.syncMapPadding();
      });
    },
    expandPanelToFull: function (reason) {
      controllers.forEach(function (c) {
        if (c.expandPanelToFull) c.expandPanelToFull(reason);
      });
    },
    collapsePanelToHalfIfFull: function () {
      controllers.forEach(function (c) {
        if (c.collapsePanelToHalfIfFull) c.collapsePanelToHalfIfFull();
      });
    },
    settlePanelAfterLocationSelect: function () {
      controllers.forEach(function (c) {
        if (c.settlePanelAfterLocationSelect) c.settlePanelAfterLocationSelect();
      });
    },
    getAdjustedCenter: function (position, mapInstance) {
      if (window.innerWidth >= BP) return position;
      return getAdjustedMapCenter(position, mapInstance);
    },
    setMode: function () {
      /* 兼容旧调用：无操作 */
    },
  };

  function applyRememberedMapCenter(map) {
    if (window.innerWidth >= BP || !map || typeof map.setCenter !== 'function') return;
    if (!window.wtbV4LastRequestedCenter) return;
    map.setCenter(getAdjustedMapCenter(window.wtbV4LastRequestedCenter, map));
  }

  function adoptExistingMap() {
    var existingMap = window.map;
    if (!existingMap || existingMap === mapBridge.map || typeof existingMap.setOptions !== 'function') return;
    window.WhereToBuyV4.registerMap(existingMap);
    applyRememberedMapCenter(existingMap);
  }

  window.addEventListener('where-to-buy-v4-map-ready', function (event) {
    var map = event.detail && event.detail.map;
    if (!map) return;
    window.WhereToBuyV4.registerMap(map);
    if (event.detail.targetCenter) {
      window.wtbV4LastRequestedCenter = event.detail.targetCenter;
    }
    applyRememberedMapCenter(map);
  });

  function toLatLngLiteral(position) {
    if (!position) return null;
    var lat = typeof position.lat === 'function' ? position.lat() : position.lat;
    var lng = typeof position.lng === 'function' ? position.lng() : position.lng;
    lat = Number(lat);
    lng = Number(lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat: lat, lng: lng };
  }

  function latLngToWorldPoint(position) {
    var siny = clamp(Math.sin((position.lat * Math.PI) / 180), -0.9999, 0.9999);
    return {
      x: 256 * (0.5 + position.lng / 360),
      y: 256 * (0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI)),
    };
  }

  function worldPointToLatLng(point) {
    var lng = (point.x / 256 - 0.5) * 360;
    var latRadians = 2 * Math.atan(Math.exp((0.5 - point.y / 256) * 2 * Math.PI)) - Math.PI / 2;
    return { lat: (latRadians * 180) / Math.PI, lng: lng };
  }

  function getMapCenterLift() {
    var controller = controllers[0];
    var visible = controller && controller.getVisibleHeight ? controller.getVisibleHeight() : (window.innerHeight || 800) * 0.5;
    return Math.round(clamp(visible * MAP_CENTER_LIFT_RATIO, 0, MAP_CENTER_LIFT_MAX));
  }

  function getAdjustedMapCenter(position, mapInstance) {
    var literal = toLatLngLiteral(position);
    if (!literal || !mapInstance || typeof mapInstance.getZoom !== 'function') return position;

    var zoom = Number(mapInstance.getZoom());
    if (!Number.isFinite(zoom)) return literal;

    var lift = getMapCenterLift();
    if (lift <= 0) return literal;

    var scale = Math.pow(2, zoom);
    var point = latLngToWorldPoint(literal);
    return worldPointToLatLng({ x: point.x, y: point.y + lift / scale });
  }

  function createController(section) {
    var clip = section.querySelector('#dealer-panel-clip');
    var sheet = section.querySelector('#mobile-bottom-sheet');
    var handle = section.querySelector('#bottom-sheet-toggle');
    var list = section.querySelector('#custom-dealer-list');
    var detail = section.querySelector('#dealer-detail-view');
    var mapWrapper = section.querySelector('#map-wrapper');
    var filterBarScroll = section.querySelector('#wtb-filter-bar-scroll');

    if (!clip || !sheet || !mapWrapper) {
      console.warn('[WhereToBuy V4] 缺少必要元素，跳过移动端初始化');
      return null;
    }

    var phase = 'flow'; // 'flow' | 'sheet'
    var viewportW = window.innerWidth || 0;
    var vh = getViewportHeight();
    var fillVh = vh;
    var peekH = PEEK_PX;
    var halfH = Math.round(vh * (3 / 5));
    var maxH = Math.round(vh - TOP_GAP);
    var detents = [peekH, halfH, maxH];
    var snapIndex = 1; // 默认 half
    var visibleH = halfH;
    var spacer = null;
    var canEngage = true;
    var pinScrollY = 0;
    var sectionFlowTopY = Math.max(0, Math.round(window.scrollY + section.getBoundingClientRect().top));
    var restoringScroll = false;
    var suspended = false;
    var visibilityFrame = null;
    var focusRestoreSnapIndex = null;
    var locationSearchInput = section.querySelector('#dealer-search-input');
    var pageLock = null;
    var transitionCover = null;
    var transitionCoverTimer = null;
    var releaseReengageTimer = null;
    var releaseHandoff = null;

    // 手势状态
    var g = null;
    var flowTouch = null;
    var history = [];
    var snapTimer = null;
    var dragFrame = null;
    var pendingDragH = null;
    var lastSheetMax = '';
    var lastSheetRenderMax = '';
    var lastSheetTy = '';
    var draggingActive = false;

    function getViewportHeight() {
      if (window.visualViewport && window.visualViewport.height) {
        return Math.round(window.visualViewport.height);
      }
      return Math.round(window.innerHeight || 800);
    }

    function setViewportVars() {
      section.style.setProperty('--wtb-v4-vh', vh + 'px');
      section.style.setProperty('--wtb-v4-fill-vh', fillVh + 'px');
    }

    function syncFillViewportHeight(allowGrow) {
      var currentH = getViewportHeight();
      fillVh = allowGrow ? Math.max(vh, currentH) : vh;
      section.style.setProperty('--wtb-v4-fill-vh', fillVh + 'px');
      syncSheetRenderHeight();
    }

    function syncSheetRenderHeight() {
      var renderMaxH = maxH + Math.max(0, fillVh - vh);
      var next = renderMaxH + 'px';
      if (next === lastSheetRenderMax) return;
      lastSheetRenderMax = next;
      clip.style.setProperty('--sheet-render-max', next);
    }

    function measure(force) {
      var nextW = window.innerWidth || viewportW || 0;
      var widthChanged = Math.abs(nextW - viewportW) > RESIZE_WIDTH_EPS;
      if (force || widthChanged) {
        viewportW = nextW;
        vh = getViewportHeight();
        fillVh = vh;
        setViewportVars();
      }
      peekH = PEEK_PX;
      halfH = Math.round(vh * (3 / 5));
      maxH = Math.round(vh - TOP_GAP);
      detents = [peekH, halfH, maxH];
    }

    function ty(h) {
      return Math.round(maxH - clamp(h, 0, maxH));
    }

    function setSheetMaxVar() {
      var next = maxH + 'px';
      if (next === lastSheetMax) return;
      lastSheetMax = next;
      clip.style.setProperty('--sheet-max', next);
    }

    function setSheetTy(h) {
      var next = ty(h) + 'px';
      if (next === lastSheetTy) return;
      lastSheetTy = next;
      clip.style.setProperty('--sheet-ty', next);
      var transform = 'translate3d(0, ' + next + ', 0)';
      clip.style.transform = transform;
      clip.style.webkitTransform = transform;
    }

    function setDraggingActive(active) {
      if (draggingActive === active) return;
      draggingActive = active;
      clip.classList.toggle('is-dragging', active);
      if (active) clip.classList.remove('is-animating');
    }

    function cancelDragFrame() {
      if (dragFrame) {
        cancelAnimationFrame(dragFrame);
        dragFrame = null;
      }
      pendingDragH = null;
      setDraggingActive(false);
    }

    function scheduleDragVisible(h) {
      visibleH = h;
      pendingDragH = h;
      setDraggingActive(true);
      if (dragFrame) return;
      dragFrame = requestAnimationFrame(function () {
        dragFrame = null;
        if (pendingDragH === null) return;
        var nextH = pendingDragH;
        pendingDragH = null;
        setSheetTy(nextH);
      });
    }

    function flushDragFrame() {
      if (dragFrame) {
        cancelAnimationFrame(dragFrame);
        dragFrame = null;
      }
      if (pendingDragH !== null) {
        setSheetTy(pendingDragH);
        pendingDragH = null;
      }
      setDraggingActive(false);
    }

    function applyVisible(h, animate) {
      cancelDragFrame();
      visibleH = h;
      clip.classList.toggle('is-animating', !!animate);
      setSheetMaxVar();
      syncSheetRenderHeight();
      setSheetTy(h);
    }

    function nearestIndex(h) {
      var best = 0;
      var bd = Infinity;
      for (var i = 0; i < detents.length; i++) {
        var d = Math.abs(h - detents[i]);
        if (d < bd) {
          bd = d;
          best = i;
        }
      }
      return best;
    }

    function syncMapPadding() {
      if (!mapBridge.map) return;
      try {
        var padding;
        var promptLift = 0;
        if (window.innerWidth >= BP) {
          padding = { top: 0, right: 0, bottom: 0, left: 0 };
        } else {
          var panelVisibleH = clamp(visibleH, 0, maxH);
          var bottomPadding = Math.round(panelVisibleH + mapPaddingLift());
          padding = { top: 0, right: 0, bottom: bottomPadding, left: 0 };
          // 提示居中在 panel 未遮挡的地图区域；half 档 panel 为 3/5 屏时，提示落在上方 2/5 的中点。
          promptLift = Math.round(panelVisibleH / 2);
        }
        section.style.setProperty('--wtb-v4-map-prompt-lift', promptLift + 'px');

        var paddingKey = [padding.top, padding.right, padding.bottom, padding.left].join(',');
        if (paddingKey !== mapBridge.lastPaddingKey) {
          mapBridge.map.setOptions({ padding: padding });
          mapBridge.lastPaddingKey = paddingKey;
        }
      } catch (e) {
        /* map 尚未就绪 */
      }
    }

    function isSectionVisible() {
      return !section.closest('[hidden]') && section.getClientRects().length > 0;
    }

    function mapPaddingLift() {
      if (window.innerWidth >= BP) return 0;
      return Math.round(clamp(visibleH * MAP_PADDING_LIFT_RATIO, 0, MAP_PADDING_LIFT_MAX));
    }

    function setStateClasses() {
      sheet.classList.toggle('at-full', snapIndex === 2);
      sheet.classList.toggle('at-peek', snapIndex === 0);
      clip.classList.toggle('at-full', snapIndex === 2);
      clip.classList.toggle('at-peek', snapIndex === 0);
    }

    function snapTo(index, animate) {
      index = clamp(index, 0, 2);
      snapIndex = index;
      applyVisible(detents[index], animate);
      setStateClasses();
      window.clearTimeout(snapTimer);
      if (animate) {
        snapTimer = window.setTimeout(syncMapPadding, SNAP_MS);
      } else {
        syncMapPadding();
      }
    }

    function expandPanelToFull(reason) {
      if (window.innerWidth >= BP || suspended || !isSectionVisible()) return;
      if ((reason === 'location' || reason === 'filter') && !hasRecentUserIntent()) return;

      if (reason === 'location') {
        focusRestoreSnapIndex = snapIndex;
      } else {
        focusRestoreSnapIndex = null;
      }

      if (phase !== 'sheet') {
        engage();
      } else {
        syncFillViewportHeight(true);
      }

      snapTo(2, true);
    }

    function collapsePanelToHalfIfFull() {
      if (window.innerWidth >= BP || suspended || !isSectionVisible()) return;
      if (phase !== 'sheet' || snapIndex !== 2) return;
      syncFillViewportHeight(true);
      snapTo(1, true);
    }

    function settlePanelAfterLocationSelect() {
      if (window.innerWidth >= BP || suspended || !isSectionVisible()) return;
      if (phase !== 'sheet') return;

      var target = focusRestoreSnapIndex;
      focusRestoreSnapIndex = null;
      if (target === null || target === undefined || target >= 2) target = 1;
      snapTo(target, true);
    }

    function ensureSpacer() {
      if (spacer) return spacer;
      spacer = document.createElement('div');
      spacer.className = 'wtb-v4-scroll-spacer';
      spacer.setAttribute('aria-hidden', 'true');
      spacer.style.overflowAnchor = 'none';
      if (section.parentNode) {
        section.parentNode.insertBefore(spacer, section.nextSibling);
      }
      return spacer;
    }

    function updateRunway() {
      var sp = ensureSpacer();
      var base = Math.round(vh * RUNWAY_VH);
      sp.style.height = base + 'px';
      sp.style.display = window.innerWidth < BP ? 'block' : 'none';
    }

    function ensureTransitionCover() {
      if (transitionCover) return transitionCover;
      transitionCover = document.createElement('div');
      transitionCover.className = 'wtb-v4-transition-cover';
      transitionCover.setAttribute('aria-hidden', 'true');
      document.body.appendChild(transitionCover);
      return transitionCover;
    }

    function hideTransitionCover() {
      window.clearTimeout(transitionCoverTimer);
      transitionCoverTimer = null;
      if (transitionCover) transitionCover.classList.remove('is-active');
    }

    function showTransitionCover() {
      var cover = ensureTransitionCover();
      cover.classList.add('is-active');
      window.clearTimeout(transitionCoverTimer);
      transitionCoverTimer = window.setTimeout(function () {
        requestAnimationFrame(function () {
          requestAnimationFrame(hideTransitionCover);
        });
      }, 80);
    }

    function setRestoringScroll() {
      restoringScroll = true;
      requestAnimationFrame(function () {
        restoringScroll = false;
      });
    }

    function lockScrollToPin() {
      if (Math.abs(window.scrollY - pinScrollY) < 1) return;
      setRestoringScroll();
      window.scrollTo(0, pinScrollY);
    }

    function lockPageScroll() {
      if (pageLock) return;
      // iOS Safari/Chrome 在 body fixed 后不会稳定跟随地址栏/工具栏收起后的视口变化
      if (IS_IOS_WEBKIT) return;
      var y = Math.max(0, Math.round(pinScrollY || window.scrollY || 0));
      pageLock = {
        y: y,
        bodyPosition: document.body.style.position,
        bodyTop: document.body.style.top,
        bodyLeft: document.body.style.left,
        bodyRight: document.body.style.right,
        bodyWidth: document.body.style.width,
        bodyOverflowY: document.body.style.overflowY,
        htmlOverflowY: document.documentElement.style.overflowY,
      };
      document.documentElement.style.overflowY = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + y + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.overflowY = 'hidden';
    }

    function unlockPageScroll(targetY) {
      if (!pageLock) return;
      var y = typeof targetY === 'number' ? targetY : pageLock.y;
      document.documentElement.style.overflowY = pageLock.htmlOverflowY;
      document.body.style.position = pageLock.bodyPosition;
      document.body.style.top = pageLock.bodyTop;
      document.body.style.left = pageLock.bodyLeft;
      document.body.style.right = pageLock.bodyRight;
      document.body.style.width = pageLock.bodyWidth;
      document.body.style.overflowY = pageLock.bodyOverflowY;
      pageLock = null;
      setRestoringScroll();
      window.scrollTo(0, y);
    }

    function engage() {
      if (phase === 'sheet') return;
      if (window.innerWidth >= BP) return;
      clearReleaseReengageTimer();
      clearReleaseHandoff();
      showTransitionCover();
      phase = 'sheet';
      measure(false);
      syncFillViewportHeight(true);
      pinScrollY = sectionFlowTopY;
      section.classList.add('is-sheet');
      document.body.classList.add('where-to-buy-v4-pinned');
      sheet.classList.add('is-active');
      applyVisible(detents[snapIndex], false);
      setStateClasses();
      lockScrollToPin();
      lockPageScroll();
      syncMapPadding();
    }

    function disengage(animate, resetToHalf) {
      if (phase === 'flow') return;
      cancelDragFrame();
      if (resetToHalf) snapIndex = 1;
      phase = 'flow';
      hideTransitionCover();
      unlockPageScroll(pinScrollY);
      syncFillViewportHeight(false);
      section.classList.remove('is-sheet');
      document.body.classList.remove('where-to-buy-v4-pinned');
      sheet.classList.remove('is-active');
      applyVisible(detents[snapIndex], animate);
      setStateClasses();
      syncMapPadding();
    }

    function suspend() {
      suspended = true;
      clearReleaseReengageTimer();
      clearReleaseHandoff();
      cancelDragFrame();
      g = null;
      flowTouch = null;
      history = [];
      canEngage = false;
      disengage(false);
      document.body.classList.remove('where-to-buy-v4-pinned');
      unlockPageScroll(pinScrollY);
      if (spacer) spacer.style.display = 'none';
    }

    function resume() {
      if (!isSectionVisible()) return;
      suspended = false;
      measure(false);
      syncFillViewportHeight(false);
      updateRunway();
      applyVisible(detents[snapIndex], false);
      sectionFlowTopY = Math.max(0, Math.round(window.scrollY + section.getBoundingClientRect().top));
      canEngage = true;
      syncMapPadding();
      onScroll();
    }

    function syncVisibilityState() {
      if (isSectionVisible()) {
        if (suspended) resume();
      } else {
        suspend();
      }
    }

    function scheduleVisibilitySync() {
      if (visibilityFrame) return;
      visibilityFrame = requestAnimationFrame(function () {
        visibilityFrame = null;
        syncVisibilityState();
      });
    }

    function blurLocationSearch() {
      if (!locationSearchInput || document.activeElement !== locationSearchInput) return;
      locationSearchInput.blur();
    }

    function isEditableElement(el) {
      if (!el || el === document.body) return false;
      if (el.isContentEditable) return true;
      return !!(
        el.matches &&
        el.matches('input, textarea, select, [contenteditable="true"], [contenteditable=""]')
      );
    }

    function isKeyboardInteractionActive() {
      if (!isEditableElement(document.activeElement) || !section.contains(document.activeElement)) return false;
      if (!window.visualViewport) return true;
      var vvHeight = Math.round(window.visualViewport.height || 0);
      return !vvHeight || vh - vvHeight > KEYBOARD_VH_DELTA || window.visualViewport.offsetTop > 0;
    }

    function clearReleaseReengageTimer() {
      window.clearTimeout(releaseReengageTimer);
      releaseReengageTimer = null;
    }

    function clearReleaseHandoff() {
      releaseHandoff = null;
    }

    function beginReleaseHandoff(startY, currentY) {
      if (typeof startY !== 'number' || typeof currentY !== 'number') return false;
      releaseHandoff = {
        startY: startY,
        startScrollY: Math.max(0, Math.round(pinScrollY || window.scrollY || 0)),
        lastY: currentY,
        maxDelta: 0,
      };
      return applyReleaseHandoff(currentY);
    }

    function applyReleaseHandoff(currentY) {
      if (!releaseHandoff || typeof currentY !== 'number') return false;
      var delta = Math.max(0, Math.round(currentY - releaseHandoff.startY));
      releaseHandoff.lastY = currentY;
      releaseHandoff.maxDelta = Math.max(releaseHandoff.maxDelta, delta);
      if (delta <= 0) return false;

      setRestoringScroll();
      window.scrollTo(0, Math.max(0, releaseHandoff.startScrollY - delta));
      return true;
    }

    function finishReleaseHandoff() {
      if (!releaseHandoff) return false;
      var moved = releaseHandoff.maxDelta;
      releaseHandoff = null;
      if (moved <= REENGAGE_GAP) scheduleReleaseReengageCheck();
      return true;
    }

    function scheduleReleaseReengageCheck() {
      clearReleaseReengageTimer();
      releaseReengageTimer = window.setTimeout(function () {
        releaseReengageTimer = null;
        requestAnimationFrame(function () {
          if (phase !== 'flow' || suspended || window.innerWidth >= BP || !isSectionVisible()) return;

          var top = section.getBoundingClientRect().top;
          if (top <= REENGAGE_GAP) {
            canEngage = true;
            sectionFlowTopY = Math.max(0, Math.round(window.scrollY + top));
            engage();
          }
        });
      }, RELEASE_REENGAGE_MS);
    }

    function flowPrelockPx() {
      return Math.round(clamp(vh * 0.08, 24, FLOW_PRELOCK_MAX_PX));
    }

    function onFlowTouchStart(e) {
      if (window.innerWidth >= BP || suspended || !isSectionVisible()) return;
      clearReleaseHandoff();
      if (phase !== 'flow' || !e.touches || e.touches.length !== 1) return;
      flowTouch = {
        lastY: e.touches[0].clientY,
      };
    }

    function onFlowTouchMove(e) {
      if (window.innerWidth >= BP || suspended || !isSectionVisible()) return;
      if (releaseHandoff && e.touches && e.touches.length === 1) {
        if (applyReleaseHandoff(e.touches[0].clientY)) e.preventDefault();
        return;
      }
      if (!flowTouch) return;
      if (phase !== 'flow' || !e.touches || e.touches.length !== 1) return;

      var y = e.touches[0].clientY;
      var dy = flowTouch.lastY - y;
      flowTouch.lastY = y;
      if (dy <= 0) return;

      var top = section.getBoundingClientRect().top;
      if (!canEngage) {
        if (top <= REENGAGE_GAP) {
          canEngage = true;
        } else {
          return;
        }
      }
      if (top > flowPrelockPx()) return;

      sectionFlowTopY = Math.max(0, Math.round(window.scrollY + top));
      engage();
      e.preventDefault();
    }

    function onFlowTouchEnd() {
      finishReleaseHandoff();
      flowTouch = null;
    }

    // ---- 滚动监听：flow 钉顶进入；sheet 阶段保护 runway / 兜底恢复 ----
    function onScroll() {
      if (!isSectionVisible()) {
        suspend();
        return;
      }
      if (suspended) {
        resume();
        return;
      }
      if (window.innerWidth >= BP) {
        if (phase === 'sheet') disengage(false);
        return;
      }
      var top = section.getBoundingClientRect().top;
      if (phase === 'flow') {
        if (top > REENGAGE_GAP) {
          clearReleaseReengageTimer();
          blurLocationSearch();
          canEngage = true;
          sectionFlowTopY = Math.max(0, Math.round(window.scrollY + top));
        }
        if (canEngage && top <= 0 && hasRecentUserIntent()) engage();
      } else {
        // sheet 阶段理论上 sticky 钉死 top=0；若被上滑带离顶部则兜底回到 flow
        if (top > 1) {
          if (isKeyboardInteractionActive()) {
            lockScrollToPin();
            return;
          }
          blurLocationSearch();
          canEngage = false;
          disengage(true, true);
          return;
        }
        if (!pageLock && !restoringScroll) lockScrollToPin();
      }
    }

    var scrollScheduled = false;
    function onScrollThrottled() {
      if (scrollScheduled) return;
      scrollScheduled = true;
      requestAnimationFrame(function () {
        scrollScheduled = false;
        if (phase === 'sheet') syncFillViewportHeight(true);
        onScroll();
      });
    }

    // ---- 触摸辅助 ----
    function isInList(t) {
      return !!list && (t === list || list.contains(t));
    }
    function isInDetail(t) {
      return !!detail && detail.classList.contains('active') && (t === detail || detail.contains(t));
    }
    function getDetailScroller(t) {
      if (!isInDetail(t)) return null;
      var el = t && (t.nodeType === 1 ? t : t.parentElement);
      while (el && el !== detail) {
        if (el.scrollHeight - el.clientHeight > 1) return el;
        el = el.parentElement;
      }
      return detail.scrollHeight - detail.clientHeight > 1 ? detail : null;
    }
    function isInFilterBar(t) {
      return !!filterBarScroll && (t === filterBarScroll || filterBarScroll.contains(t));
    }
    function getFilterOptionScroller(t) {
      var el = t && (t.nodeType === 1 ? t : t.parentElement);
      var fallback = null;
      while (el && el !== section) {
        if (
          el.matches &&
          el.matches(
            '.wtb-filter-autocomplete__dropdown, .custom-select-series-options-container, .custom-select-series-options'
          )
        ) {
          if (!fallback) fallback = el;
          if (el.scrollHeight - el.clientHeight > 1) return el;
        }
        el = el.parentElement;
      }
      return fallback;
    }
    function getLocationAutocompleteScroller(t) {
      var el = t && (t.nodeType === 1 ? t : t.parentElement);
      while (el && el !== section) {
        if (el.matches && el.matches('.wtb-location-autocomplete')) return el;
        el = el.parentElement;
      }
      return null;
    }
    function isInSheet(t) {
      return t === sheet || sheet.contains(t);
    }
    function isInMap(t) {
      return t === mapWrapper || mapWrapper.contains(t);
    }

    function releaseToPageScroll(startY, currentY) {
      blurLocationSearch();
      canEngage = false;
      disengage(true, true);
      if (!beginReleaseHandoff(startY, currentY)) scheduleReleaseReengageCheck();
    }

    function recordTouch(y) {
      var t = nowMs();
      history.push({ y: y, t: t });
      history = history.filter(function (p) {
        return t - p.t < 120;
      });
      if (history.length > 12) history = history.slice(-12);
    }

    function velocity() {
      if (history.length < 2) return 0;
      var a = history[0];
      var b = history[history.length - 1];
      var dt = b.t - a.t;
      if (dt <= 6) return 0;
      return (a.y - b.y) / dt; // 正值 = 手指上移（展开）
    }

    function scrollerShouldScroll(scroller, dy) {
      if (!scroller) return false;
      var top = scroller.scrollTop;
      var max = scroller.scrollHeight - scroller.clientHeight;
      if (dy > 0) return top < max - 0.5; // 继续上滑看列表
      if (dy < 0) return top > 0.5; // 下滑回列表顶部
      return false;
    }

    // 列表是否应当原生滚动（half/full 均允许，边界处再交给面板拖拽）
    function listShouldScroll(dy) {
      return scrollerShouldScroll(list, dy);
    }

    function detailShouldScroll(scroller, dy) {
      return scrollerShouldScroll(scroller, dy);
    }

    function beginDragFrom(y) {
      g.startY = y;
      g.startVisible = visibleH;
      clip.classList.remove('is-animating');
    }

    function onTouchStart(e) {
      if (suspended || !isSectionVisible()) return;
      if (phase !== 'sheet') return;
      if (e.touches.length >= 2) {
        g = { mode: 'map' };
        return;
      }
      g = {
        mode: null,
        decided: false,
        startY: e.touches[0].clientY,
        startX: e.touches[0].clientX,
        startVisible: visibleH,
        startSnap: snapIndex,
        target: e.target,
      };
      history = [];
      recordTouch(g.startY);
    }

    function onTouchMove(e) {
      if (suspended || !isSectionVisible()) return;
      if (phase !== 'sheet') return; // flow：交给原生滚动
      if (e.touches.length >= 2) {
        if (g) g.mode = 'map';
        return; // 双指：交给 Google 地图
      }
      // 进入 sheet 发生在手势中途时，惰性初始化
      if (!g) {
        g = {
          mode: null,
          decided: false,
          startY: e.touches[0].clientY,
          startX: e.touches[0].clientX,
          startVisible: visibleH,
          startSnap: snapIndex,
          target: e.target,
        };
        history = [];
      }
      if (g.mode === 'map') return;
      if (g.mode === 'filter-scroll') return;
      if (g.mode === 'filter-options') return;
      if (g.mode === 'location-options') return;

      var y = e.touches[0].clientY;
      var x = e.touches[0].clientX;
      recordTouch(y);
      var dy = g.startY - y; // 上滑为正
      var dx = x - g.startX;

      if (!g.decided) {
        if (Math.abs(dy) < DECIDE_PX && Math.abs(dx) < DECIDE_PX) return;
        g.decided = true;

        if (getLocationAutocompleteScroller(g.target)) {
          g.mode = 'location-options';
          return;
        }

        var filterOptionScroller = getFilterOptionScroller(g.target);
        if (filterOptionScroller) {
          g.mode = 'filter-options';
          g.filterOptionScroller = filterOptionScroller;
          return;
        }

        if (isInFilterBar(g.target) && Math.abs(dx) >= Math.abs(dy)) {
          g.mode = 'filter-scroll';
          return;
        }

        if (isInDetail(g.target)) {
          g.detailScroller = getDetailScroller(g.target);
          g.mode = detailShouldScroll(g.detailScroller, dy) ? 'detail' : 'drag';
        } else if (isInList(g.target)) {
          g.mode = listShouldScroll(dy) ? 'list' : 'drag';
        } else if (isInMap(g.target)) {
          g.mode = 'map-page';
        } else if (snapIndex === 0 && dy < 0) {
          // peek 档继续下拉 → 恢复整页（交还原生滚动）
          g.mode = 'release';
        } else {
          g.mode = 'drag';
        }

        if (g.mode === 'release') {
          releaseToPageScroll(g.startY, y); // 退出固定后恢复到 half 档，并随页面下滑离场
          if (releaseHandoff) e.preventDefault();
          return;
        }
        if (g.mode === 'map-page') {
          if (dy < 0) {
            if (-dy >= MAP_PAGE_RELEASE_PX) {
              // 地图已吸顶时，下拉超过缓冲后才交还页面滚动，避免轻触误触发恢复。
              releaseToPageScroll(g.startY, y);
              if (releaseHandoff) e.preventDefault();
              return;
            }
            e.preventDefault();
            return;
          }
          // 地图吸顶后，上滑不展开 panel，也不继续滚到后续内容。
          e.preventDefault();
          return;
        }
        if (g.mode === 'detail') return; // 原生详情滚动
        if (g.mode === 'list') return; // 原生列表滚动
        beginDragFrom(y);
      }

      if (g.mode === 'map-page') {
        if (dy < 0) {
          if (-dy >= MAP_PAGE_RELEASE_PX) {
            if (phase === 'sheet') releaseToPageScroll(g.startY, y);
            if (releaseHandoff) e.preventDefault();
            return;
          }
          e.preventDefault();
          return;
        }
        e.preventDefault();
        return;
      }

      if (g.mode === 'list') {
        if (listShouldScroll(g.startY - y)) return;
        // 到达列表边界 → 转为拖盖
        g.mode = 'drag';
        beginDragFrom(y);
      }

      if (g.mode === 'detail') {
        if (detailShouldScroll(g.detailScroller, g.startY - y)) return;
        // 到达详情边界 → 转为拖盖
        g.mode = 'drag';
        beginDragFrom(y);
      }

      if (g.mode === 'drag') {
        var h = g.startVisible + (g.startY - y);
        if (h > maxH) h = maxH + (h - maxH) * 0.18; // 顶部橡皮筋
        if (h < peekH) h = peekH + (h - peekH) * 0.3; // 底部橡皮筋
        scheduleDragVisible(h);
        e.preventDefault();
      }
    }

    function onTouchEnd(e) {
      if (suspended || !isSectionVisible()) {
        g = null;
        return;
      }
      if (phase !== 'sheet') {
        g = null;
        return;
      }
      if (e.touches && e.touches.length > 0) return; // 仍有手指（如双指松开一指）
      if (g && g.mode === 'drag') {
        flushDragFrame();
        syncFillViewportHeight(true);
        var v = velocity();
        var target;
        if (Math.abs(v) > VEL) {
          // 快速轻扫：相对起始档移动一档，并兼顾落点
          var ni = nearestIndex(visibleH);
          if (v > 0) target = Math.max(Math.min(2, g.startSnap + 1), ni);
          else target = Math.min(Math.max(0, g.startSnap - 1), ni);
        } else {
          target = nearestIndex(visibleH);
        }
        snapTo(target, true);
      }
      cancelDragFrame();
      g = null;
      history = [];
    }

    // ---- 把手点击：循环切换三档 ----
    if (handle) {
      handle.addEventListener('click', function () {
        if (phase !== 'sheet') return;
        snapTo((snapIndex + 1) % 3, true);
      });
    }

    // ---- detail 详情打开/关闭 ----
    var onDetailOpen = function () {
      if (window.innerWidth >= BP) return;
      if (phase !== 'sheet') engage();
      snapTo(2, true);
    };
    var onDetailClose = function () {
      if (window.innerWidth >= BP || phase !== 'sheet') return;
      snapTo(1, true);
    };
    document.addEventListener('where-to-buy-detail-opened', onDetailOpen);
    document.addEventListener('where-to-buy-detail-closed', onDetailClose);

    var visibilityRoot = section.closest('.shopify-section') || section;
    if (window.MutationObserver) {
      var visibilityObserver = new MutationObserver(scheduleVisibilitySync);
      visibilityObserver.observe(visibilityRoot, {
        attributes: true,
        attributeFilter: ['hidden', 'style', 'class'],
      });
    }

    // ---- 事件绑定（capture 以先于 Google 地图分流单/双指） ----
    window.addEventListener('scroll', onScrollThrottled, { passive: true });
    window.addEventListener('touchstart', onFlowTouchStart, { passive: true, capture: true });
    window.addEventListener('touchmove', onFlowTouchMove, { passive: false, capture: true });
    window.addEventListener('touchend', onFlowTouchEnd, { passive: true, capture: true });
    window.addEventListener('touchcancel', onFlowTouchEnd, { passive: true, capture: true });
    section.addEventListener('touchstart', onTouchStart, { passive: true, capture: true });
    section.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });
    section.addEventListener('touchend', onTouchEnd, { passive: true, capture: true });
    section.addEventListener('touchcancel', onTouchEnd, { passive: true, capture: true });

    var resizeTimer = null;
    var resizeFrame = null;
    function onViewportResize() {
      if (resizeFrame) return;
      resizeFrame = requestAnimationFrame(function () {
        resizeFrame = null;
        if (window.innerWidth >= BP) {
          window.clearTimeout(resizeTimer);
          disengage(false);
          if (spacer) spacer.style.display = 'none';
          return;
        }

        syncFillViewportHeight(phase === 'sheet');

        var nextW = window.innerWidth || viewportW || 0;
        var widthChanged = Math.abs(nextW - viewportW) > RESIZE_WIDTH_EPS;
        if (!widthChanged) return;

        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(function () {
          if (window.innerWidth >= BP) {
            disengage(false);
            if (spacer) spacer.style.display = 'none';
            return;
          }
          measure(false);
          syncFillViewportHeight(phase === 'sheet');
          updateRunway();
          if (phase === 'sheet') snapTo(snapIndex, false);
          else applyVisible(halfH, false);
          syncMapPadding();
        }, 120);
      });
    }
    window.addEventListener('resize', onViewportResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onViewportResize);
      window.visualViewport.addEventListener('scroll', onViewportResize);
    }

    // ---- 初始化 ----
    measure(true);
    syncFillViewportHeight(false);
    updateRunway();
    applyVisible(halfH, false);
    syncMapPadding();
    // 初始进入页面时保持 flow；真实滚动/触摸到顶后再进入 sheet。

    return {
      getVisibleHeight: function () {
        return visibleH;
      },
      syncMapPadding: syncMapPadding,
      expandPanelToFull: expandPanelToFull,
      collapsePanelToHalfIfFull: collapsePanelToHalfIfFull,
      settlePanelAfterLocationSelect: settlePanelAfterLocationSelect,
    };
  }

  function initAll() {
    if (window.innerWidth >= BP) return;
    document.querySelectorAll('.where-to-buy-v4[data-wtb-v4-init]').forEach(function (section) {
      if (section._wtbV4Inited) return;
      var c = createController(section);
      if (c) {
        section._wtbV4Inited = true;
        controllers.push(c);
      }
    });
    adoptExistingMap();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // 桌面 → 移动端 resize 时补充初始化
  var globalResizeTimer = null;
  window.addEventListener('resize', function () {
    if (window.innerWidth >= BP) return;
    window.clearTimeout(globalResizeTimer);
    globalResizeTimer = window.setTimeout(initAll, 200);
  });
})();
